import { Injectable, OnDestroy } from '@angular/core';

interface HSLColor {
  h: number;
  s: number;
  l: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: HSLColor;
  alpha: number;
  decay: number;
  history: { x: number; y: number }[];
}

interface Launcher {
  x: number;
  y: number;
  vx: number;
  vy: number;
  targetY: number;
  color: HSLColor;
  history: { x: number; y: number }[];
}

@Injectable({
  providedIn: 'root'
})
export class FireworksService implements OnDestroy {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private animationFrameId: number | null = null;
  private timeoutId: any = null;
  
  private particles: Particle[] = [];
  private launchers: Launcher[] = [];
  
  private isRunning = false;
  
  private width = 0;
  private height = 0;
  private resizeListener: () => void;

  private colors: HSLColor[] = [
    { h: 0, s: 100, l: 60 },    // Red
    { h: 220, s: 100, l: 60 },  // Blue
    { h: 0, s: 0, l: 100 },     // White
    { h: 45, s: 100, l: 60 },   // Gold
    { h: 280, s: 100, l: 65 },  // Purple
    { h: 180, s: 100, l: 50 },  // Cyan
    { h: 320, s: 100, l: 60 },  // Pink
    { h: 25, s: 100, l: 60 }    // Orange
  ];

  constructor() {
    this.resizeListener = this.onResize.bind(this);
  }

  public start() {
    if (this.isRunning) return; // Prevent duplicate instances
    this.isRunning = true;

    this.initCanvas();
    this.loop();

    // Stop after exactly 30 seconds
    this.timeoutId = setTimeout(() => {
      this.stop();
    }, 30000);
  }

  public stop() {
    this.isRunning = false;
    
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
    
    window.removeEventListener('resize', this.resizeListener);

    this.canvas = null;
    this.ctx = null;
    this.particles = [];
    this.launchers = [];
  }

  ngOnDestroy() {
    this.stop();
  }

  private initCanvas() {
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'global-fireworks-canvas';
    
    // UI Safety Requirements
    this.canvas.style.position = 'fixed';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.width = '100vw';
    this.canvas.style.height = '100vh';
    this.canvas.style.pointerEvents = 'none';
    this.canvas.style.zIndex = '5'; // Above background, below forms (z-10)

    document.body.appendChild(this.canvas);
    
    this.ctx = this.canvas.getContext('2d');
    
    window.addEventListener('resize', this.resizeListener);
    this.onResize();
  }

  private onResize() {
    if (this.canvas) {
      this.width = window.innerWidth;
      this.height = window.innerHeight;
      this.canvas.width = this.width;
      this.canvas.height = this.height;
    }
  }

  private randomColor(): HSLColor {
    return this.colors[Math.floor(Math.random() * this.colors.length)];
  }

  private launchFirework() {
    const x = Math.random() * (this.width * 0.8) + this.width * 0.1;
    const y = this.height;
    const targetY = Math.random() * (this.height * 0.5) + this.height * 0.1;
    const vx = (Math.random() - 0.5) * 2;
    const vy = -Math.random() * 4 - 8;
    const color = this.randomColor();

    this.launchers.push({
      x,
      y,
      vx,
      vy,
      targetY,
      color,
      history: []
    });
  }

  private createExplosion(x: number, y: number, color: HSLColor, isMulticolor: boolean = false) {
    const particleCount = Math.floor(Math.random() * 40) + 80; // 80 - 120 particles
    const baseColor = color;

    for (let i = 0; i < particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 8 + 2; // Increased explosion radius and spread
      const pColor = isMulticolor ? this.randomColor() : baseColor;
      
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: Math.random() * 2 + 1.5, // Larger, visually rich
        color: pColor,
        alpha: 1,
        decay: Math.random() * 0.015 + 0.015,
        history: []
      });
    }
  }

  private loop = () => {
    if (!this.isRunning || !this.ctx || !this.canvas) return;

    this.ctx.clearRect(0, 0, this.width, this.height);
    this.ctx.globalCompositeOperation = 'lighter'; // Additive blending for glow

    // Randomly launch fireworks
    if (Math.random() < 0.05) { // Roughly 3 per second at 60fps
      this.launchFirework();
    }
    
    // Celebration bursts (random explosions without launcher)
    if (Math.random() < 0.01) {
       const x = Math.random() * this.width;
       const y = Math.random() * (this.height * 0.6);
       this.createExplosion(x, y, this.randomColor(), Math.random() > 0.5);
    }

    // Update and draw launchers
    for (let i = this.launchers.length - 1; i >= 0; i--) {
      const launcher = this.launchers[i];
      
      launcher.history.push({ x: launcher.x, y: launcher.y });
      if (launcher.history.length > 5) {
        launcher.history.shift();
      }

      launcher.vy += 0.05; // Gravity
      launcher.x += launcher.vx;
      launcher.y += launcher.vy;

      // Draw launcher trail
      this.ctx.beginPath();
      if (launcher.history.length > 0) {
        this.ctx.moveTo(launcher.history[0].x, launcher.history[0].y);
        for (let j = 1; j < launcher.history.length; j++) {
          this.ctx.lineTo(launcher.history[j].x, launcher.history[j].y);
        }
      } else {
        this.ctx.moveTo(launcher.x, launcher.y);
        this.ctx.lineTo(launcher.x, launcher.y);
      }
      this.ctx.strokeStyle = `hsl(${launcher.color.h}, ${launcher.color.s}%, ${launcher.color.l}%)`;
      this.ctx.lineWidth = 3;
      this.ctx.stroke();

      // Check if reached target or starting to fall
      if (launcher.vy >= 0 || launcher.y <= launcher.targetY) {
        this.createExplosion(launcher.x, launcher.y, launcher.color, Math.random() > 0.7);
        this.launchers.splice(i, 1);
      }
    }

    // Update and draw particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];

      p.history.push({ x: p.x, y: p.y });
      if (p.history.length > 6) {
        p.history.shift();
      }

      p.vy += 0.08; // Gravity
      p.vx *= 0.95; // Friction
      p.vy *= 0.95; // Friction
      p.x += p.vx;
      p.y += p.vy;
      p.alpha -= p.decay;

      if (p.alpha <= 0) {
        this.particles.splice(i, 1);
        continue;
      }

      // Draw particle trail
      this.ctx.beginPath();
      if (p.history.length > 0) {
        this.ctx.moveTo(p.history[0].x, p.history[0].y);
        for (let j = 1; j < p.history.length; j++) {
          this.ctx.lineTo(p.history[j].x, p.history[j].y);
        }
      } else {
        this.ctx.moveTo(p.x, p.y);
        this.ctx.lineTo(p.x, p.y);
      }
      
      this.ctx.strokeStyle = `hsla(${p.color.h}, ${p.color.s}%, ${p.color.l}%, ${p.alpha})`;
      this.ctx.lineWidth = p.size;
      this.ctx.lineCap = 'round';
      this.ctx.stroke();
      
      // Core glow
      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y, p.size * 1.5, 0, Math.PI * 2);
      this.ctx.fillStyle = `hsla(${p.color.h}, ${p.color.s}%, ${p.color.l}%, ${p.alpha * 0.3})`;
      this.ctx.fill();
    }

    this.animationFrameId = requestAnimationFrame(this.loop);
  }
}
